import { useCallback, useEffect, useState } from 'react'
import { Button, Checkbox, Form, Input, Select, Table, TableProps } from 'antd'
import defaultParams from '../../assets/constants/default_params'
import { useLoading } from '../../context/LoadingContext'
import { apiAddInvoiceByForm, apiGetListCompanies, apiGetListInvoiceTemplates } from '../../api'
import { filterOptionByLabel } from '../../utils/filter-utils'
import { ICompany } from '../../models/company.modal'
import { formatCurrency } from '../../utils/string-utils'
import { useSelector } from 'react-redux'
import { authSelector } from '../../redux/slices/authSlice'
import { toastError, toastSuccess } from '../../utils/toast'
import { formatDateTime } from '../../utils/entity-utils'
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc";
import { renderInvoiceStatus } from './history'

dayjs.extend(utc);

export default function InvoiceCreate() {
    const [companyOptions, setCompanyOptions] = useState<{ label: string, value: Number }[]>([])
    const [invoiceTemplates, setInvoiceTemplates] = useState<any>([])
    const [customerName, setCustomerName] = useState<string>('')
    const [cpCode, setCpCode] = useState<string>('')
    const [isAnonymousCustomer, setIsAnonymousCustomer] = useState<boolean>(false)
    const [listInvoices, setListInvoices] = useState<any[]>([])
    const { setIsLoading } = useLoading()
    const [formAddInvoiceByForm] = Form.useForm()
    const { account } = useSelector(authSelector)

    const fetchCompanies = useCallback(async () => {
        try {
            const resp = await apiGetListCompanies({ limit: defaultParams.MAX_LIMIT })
            if (resp.data != null) {
                const options = resp.data.companies.map((item: ICompany) => ({
                    label: item.name,
                    value: item.code,
                }))
                setCompanyOptions(options)
            } else {
                setCompanyOptions([])
            }
        } catch (error) {
            console.log(error)
        }
    }, [])

    const fetchInvoiceTemplates = useCallback(async () => {
        if (!account?.canGetListInvoiceTemplates) return
        try {
            const respInvoice = await apiGetListInvoiceTemplates()
            setInvoiceTemplates(respInvoice.data.templates)
        } catch (error) {
            console.log(error)
        }
    }, [account])

    useEffect(() => {
        fetchCompanies()
        fetchInvoiceTemplates()
        const savedCpCode = sessionStorage.getItem('lastInvoiceCpCode')
        const savedTemplateId = sessionStorage.getItem('lastInvoiceTemplateId')
        const fields: any = {}
        if (savedCpCode) {
            fields.cpCode = savedCpCode
            setCpCode(savedCpCode)
        }
        if (savedTemplateId) fields.invoice_template_id = Number(savedTemplateId)
        if (Object.keys(fields).length > 0) {
            formAddInvoiceByForm.setFieldsValue(fields)
        }
    }, [fetchCompanies, fetchInvoiceTemplates, cpCode])

    const handleAddInvoiceByForm = async (values: any) => {
        try {
            setIsLoading(true)
            const resp = await apiAddInvoiceByForm({
                ...values,
                customer_name: values.customer_name.trim()?.toUpperCase(),
                department: values.department?.trim(),
                address: values.address?.trim(),
                tax_code: values.tax_code?.trim(),
                identity_card: values.identity_card?.trim(),
            })
            if (!resp.data?.invoice) {
                toastError('Thêm hóa đơn thất bại!')
                return
            }
            toastSuccess('Thêm hóa đơn thành công!')
            formAddInvoiceByForm.resetFields()
            formAddInvoiceByForm.setFieldsValue({ cpCode: values.cpCode, invoice_template_id: values.invoice_template_id })
            setCustomerName('')
            setIsAnonymousCustomer(false)
            setListInvoices((prev: any) => [resp.data.invoice, ...prev])
        } catch (error) {
            console.log(error)
            toastError('Có lỗi xảy ra, vui lòng thử lại sau')
        } finally {
            setIsLoading(false)
        }
    }

    const invoiceColumns: TableProps<any>['columns'] = [
        {
            key: 'cpCode',
            title: 'Đối tác',
            dataIndex: 'cpCode',
        },
        {
            key: 'owner_by',
            title: 'Nhân sự',
            dataIndex: 'owner_by',
        },
        {
            key: 'customer_name',
            title: 'Khách hàng',
            dataIndex: 'customer_name',
        },
        {
            key: 'price',
            title: 'Tổng tiền',
            render: invoice => invoiceTemplates.find((template: any) => template.id === invoice.invoice_template_id)
                ? formatCurrency(invoiceTemplates.find((template: any) => template.id === invoice.invoice_template_id)?.total_amount || 0)
                : 'N/A',
        },
        {
            key: 'department',
            title: 'Đơn vị',
            dataIndex: 'department',
        },
        {
            key: 'address',
            title: 'Địa chỉ',
            dataIndex: 'address',
        },
        {
            key: 'tax_code',
            title: 'Mã số thuế',
            dataIndex: 'tax_code',
        },
        {
            key: 'identity_card',
            title: 'CCCD',
            dataIndex: 'identity_card',
        },
        {
            key: 'status',
            title: 'Trạng thái',
            dataIndex: 'status',
            render: (status) => renderInvoiceStatus(status),
        },
        {
            key: 'created_at',
            title: 'Ngày tạo',
            dataIndex: 'created_at',
            render: (date) => formatDateTime(date),
        },
    ]

    return (
        <div>
            <h2>Xuất hóa đơn</h2>
            <Form form={formAddInvoiceByForm} onFinish={handleAddInvoiceByForm} layout="vertical" autoComplete='off'>
                <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
                    <Form.Item name='cpCode' label='Công ty' style={{ flex: 1 }}
                        rules={[{ required: true, message: 'Vui lòng chọn công ty' }]}>
                        <Select
                            style={{ minWidth: 170 }}
                            showSearch
                            placeholder="Chọn công ty"
                            options={companyOptions}
                            filterOption={filterOptionByLabel}
                            onChange={(value) => {
                                sessionStorage.setItem('lastInvoiceCpCode', String(value))
                                formAddInvoiceByForm.setFieldsValue({ cpCode: value })
                                setCpCode(value)
                                const template = invoiceTemplates.find((config: any) => config.id === formAddInvoiceByForm.getFieldValue('invoice_template_id'))
                                if (template?.cpCode !== value) {
                                    sessionStorage.setItem('lastInvoiceTemplateId', '')
                                    formAddInvoiceByForm.setFieldsValue({ invoice_template_id: '' })
                                }
                            }}
                        />
                    </Form.Item>
                    <Form.Item name='invoice_template_id' label='Template hóa đơn' style={{ flex: 1 }}
                        rules={[{ required: true, message: 'Vui lòng chọn template hóa đơn!' }]}>
                        <Select
                            showSearch
                            placeholder="Chọn template hóa đơn"
                            options={invoiceTemplates.filter((config: any) => config.cpCode === cpCode).map((config: any) => ({
                                label: config.alias,
                                value: config.id,
                            }))}
                            filterOption={filterOptionByLabel}
                            onChange={(value) => {
                                sessionStorage.setItem('lastInvoiceTemplateId', String(value))
                                formAddInvoiceByForm.setFieldsValue({ invoice_template_id: value })
                            }}
                        />
                    </Form.Item>
                </div>
                <Form.Item name='customer_name' label='Khách hàng'
                    rules={[{ required: true, message: 'Vui lòng nhập tên khách hàng!' }]}>
                    <Input
                        disabled={isAnonymousCustomer}
                        value={customerName}
                        onChange={e => {
                            setCustomerName(e.target.value)
                            formAddInvoiceByForm.setFieldsValue({ customer_name: e.target.value })
                        }} />
                    <Checkbox
                        checked={isAnonymousCustomer}
                        style={{ marginTop: 10, fontSize: 12 }}
                        onChange={() => {
                            const next = !isAnonymousCustomer
                            setIsAnonymousCustomer(next)
                            if (next) {
                                setCustomerName('KHÁCH HÀNG KHÔNG CUNG CẤP THÔNG TIN')
                                formAddInvoiceByForm.setFieldsValue({
                                    customer_name: 'KHÁCH HÀNG KHÔNG CUNG CẤP THÔNG TIN',
                                    department: '',
                                    address: '',
                                    tax_code: '',
                                    identity_card: '',
                                })
                            } else {
                                setCustomerName('')
                                formAddInvoiceByForm.setFieldsValue({ customer_name: '' })
                            }
                        }}
                    >KHÁCH HÀNG KHÔNG CUNG CẤP THÔNG TIN</Checkbox>
                </Form.Item>
                <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
                    <Form.Item name='department' label='Đơn vị' style={{ flex: 1 }}>
                        <Input disabled={isAnonymousCustomer} />
                    </Form.Item>
                    <Form.Item name='address' label='Địa chỉ' style={{ flex: 1 }}>
                        <Input disabled={isAnonymousCustomer} />
                    </Form.Item>
                </div>
                <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
                    <Form.Item name='tax_code' label='Mã số thuế' style={{ flex: 1 }}>
                        <Input disabled={isAnonymousCustomer} />
                    </Form.Item>
                    <Form.Item name='identity_card' label='CCCD' style={{ flex: 1 }}>
                        <Input disabled={isAnonymousCustomer} />
                    </Form.Item>
                </div>
                <div style={{ textAlign: 'right' }}>
                    <Button type='primary' htmlType='submit'>Xuất</Button>
                </div>
            </Form>

            {listInvoices.length > 0 && (
                <div style={{ marginTop: 40 }}>
                    <h3>Danh sách hóa đơn vừa tạo</h3>
                    <Table
                        columns={invoiceColumns}
                        dataSource={listInvoices}
                        rowKey="id"
                        scroll={{ x: 'max-content' }}
                        pagination={false}
                    />
                </div>
            )}
        </div>
    )
}
